

































































































































































































































































import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import { merge, omit } from 'lodash';

import { ErrorManager } from '@/models/error';
import { IPaymentRequisition, PaymentRequisitionFilterParams } from '@/models';

import { createPdf, createFile } from '@/utils/helpers';
import { IPaymentRequisitionFetchActionPayload } from '@/store/payment-requisition';
import { DataTableItemProps } from 'vuetify';

import mixins from '@/utils/mixins';
import Functions from '@/mixins/functions';
import Auth from '@/mixins/auth';

import PaymentRequisitionActivityDialog from '@/components/payment-requisition/PaymentRequisitionActivityDialog.vue';
import PaymentRequisitionDetailFormDialog from '@/components/payment-requisition/PaymentRequisitionDetailFormDialog.vue';
import PaymentRequisitionSearchFilter from '@/components/payment-requisition/PaymentRequisitionSearchFilter.vue';
import ALPage from '@/components/core/Page/ALPage.vue';
import PaymentRequisitionStatusDisplay from '@/components/requisition-common/PaymentRequisitionStatusDisplay.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import PaymentRequisitionBulkUpdateOwnerDialog from '@/components/payment-requisition/PaymentRequisitionBulkUpdateOwnerDialog.vue';

export default mixins(Functions, Auth).extend({
  name: 'PaymentRequisitionListPage',

  components: {
    PaymentRequisitionDetailFormDialog,
    PaymentRequisitionActivityDialog,
    PaymentRequisitionSearchFilter,
    ALPage,
    PaymentRequisitionStatusDisplay,
    ErrorAlert,
    PaymentRequisitionBulkUpdateOwnerDialog
  },

  data() {
    return {
      loading: false,
      exportLoading: false,
      formDialog: false,
      deleteDialog: false,
      sortBy: 'id',
      sortDesc: true,

      params: {
        search: this.$route.query.search as string,
        page: Number(this.$route.query.page ?? 1)
      } as IPaymentRequisitionFetchActionPayload,

      errorMessage: '',
      detailErrorMessage: '',

      headers: [
        {
          text: '',
          sortable: false,
          width: '1px',
          class: 'pr-0 mr-n3',
          value: 'data-table-select'
        },
        { text: 'Req #', sortable: false, value: 'id', width: 70 },
        {
          text: 'Requestor',
          sortable: false,
          value: 'owner.fullName',
          width: 200
        },
        { text: 'Supplier', sortable: false, value: 'supplier', width: 300 },

        { text: 'P.O #', sortable: false, value: 'po_number' },

        {
          text: 'Items',
          sortable: false,
          value: 'num_items',
          align: 'start',
          width: 60
        },
        {
          text: 'Currency',
          sortable: false,
          value: 'currency',
          align: 'center',
          width: 60
        },
        {
          text: 'Amount',
          sortable: false,
          value: 'amount',
          align: 'end',
          width: 130
        },
        {
          text: 'Needed By',
          sortable: false,
          value: 'required_date',
          width: 155
        },
        { text: '', sortable: false, value: 'is_urgent', width: 60 },
        {
          text: 'Status',
          sortable: false,
          value: 'status',
          align: 'center',
          width: 200
        },
        {
          text: '',
          sortable: false,
          value: 'actions',
          align: 'center',
          width: 100
        }
      ],

      isSearchFilterActive: false,

      selectedRequisitions: [] as IPaymentRequisition[],
      bulkActionsMenu: false
    };
  },

  mounted() {
    // update `this.params` from the route query object.
    // omitting `search` and `page` as they are already set in the data vue option
    merge(this.params, omit(this.$route.query, ['page', 'search']));

    this.loading = true;
    this.searchPaymentRequisitions(this.params);
  },

  computed: {
    ...mapGetters({
      paymentRequisitions: 'paymentRequisition/getPaymentRequisitions',
      numQuotes: 'paymentRequisition/getPaymentRequisitionCount'
    }),

    errorOccurred(): boolean {
      return this.errorMessage.length > 0;
    },

    extraFiltersApplied(): boolean {
      return Object.values(omit(this.$route.query, ['page', 'search'])).some(
        v => !!v
      );
    },

    isRequisitionSelected(): boolean {
      return this.selectedRequisitions.length != 0;
    },

    listPageHeight(): number {
      return this.isUserPaymentRequisitionAdmin ? 73 : 37;
    }
  },

  watch: {
    'params.search'(search: string) {
      this.debounceFetchPaymentRequisitions({
        ...this.params,
        search: search,
        page: 1
      }).then(() => {
        this.params.page = 1;
      });
    }
  },

  methods: {
    moment,

    ...mapActions({
      fetchPaymentRequisitions: 'paymentRequisition/fetchPaymentRequisitions',
      exportPaymentRequisitionPdf:
        'paymentRequisition/exportPaymentRequisitionPdf',
      exportPaymentRequisitions: 'paymentRequisition/exportPaymentRequisitions'
    }),

    selectPaymentRequisition(
      paymentRequisition: IPaymentRequisition,
      props: DataTableItemProps
    ) {
      if (this.isRequisitionSelected) {
        props.select(!props.isSelected);
      } else {
        this.routeToPaymentRequisition(paymentRequisition.id!);
      }
    },

    routeToPaymentRequisition(paymentRequisitionId: number) {
      this.$router.push({
        name: 'payment-requisition-detail',
        params: {
          id: String(paymentRequisitionId)
        }
      });
    },

    exportPdf(paymentRequisition: IPaymentRequisition) {
      this.loading = true;
      this.exportPaymentRequisitionPdf(paymentRequisition.id)
        .then((data: Blob) => {
          const fileName = `${
            paymentRequisition.id
          }-payment-requisition-${moment(new Date()).format(
            'YYYY-MM-DD-HH-mm-ss'
          )}`;
          createPdf(data, fileName);
        })
        .catch(error => {
          this.errorMessage = ErrorManager.extractApiError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    createPaymentRequisition() {
      (this as any).$refs.paymentRequisitionDetailFormDialog.open();
    },

    openActivitiesDialog(paymentRequisition: IPaymentRequisition) {
      const dialog = this.$refs
        .paymentRequisitionActivityDialog as InstanceType<
        typeof PaymentRequisitionActivityDialog
      >;
      dialog.open(paymentRequisition);
    },

    clearAllErrors(): void {
      this.errorMessage = '';
    },

    async debounceFetchPaymentRequisitions(
      params: IPaymentRequisitionFetchActionPayload
    ) {
      this.clearAllErrors();
      this.loading = true;

      return await this.debounce(this.searchPaymentRequisitions, 200)(params);
    },

    /**
     * @param {IPaymentRequisitionFetchActionPayload} params
     */
    searchPaymentRequisitions(
      queryParams: IPaymentRequisitionFetchActionPayload
    ) {
      const params = this.cleanFilterParams(queryParams);

      /* this will update the URL query based on param values. */
      this.$router
        .replace({
          name: 'payment-requisition-list',
          query: params as Record<string, any>
        })
        .catch((e: Error) => e); //catch navigation duplication error

      return this.fetchPaymentRequisitions(params)
        .catch(error => {
          this.errorMessage = ErrorManager.extractApiError(error);
          if (error.response) {
            if (error.response.data.detail) {
              this.detailErrorMessage = error.response.data.detail;
            }
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    refreshSearchFilter(params: PaymentRequisitionFilterParams) {
      this.clearAllErrors();

      this.loading = true;
      this.searchPaymentRequisitions({
        // reset page to 1 when updating params
        page: 1,
        search: this.params.search,
        ...params
      }).then(() => {
        // update params object
        this.params.state = params.state;
        this.params.owner = params.owner;
        this.params.po_number = params.po_number;
        this.params.page = 1;
      });
    },

    cleanFilterParams(params: PaymentRequisitionFilterParams) {
      if (!this.isUserPaymentRequisitionAdmin) {
        delete params.owner;
      }
      return params;
    },

    onUpdatePage(newPage: number) {
      if (newPage != this.params.page) {
        this.debounceFetchPaymentRequisitions({
          ...this.params,
          page: newPage
        }).then(() => {
          this.params.page = newPage;
        });
      }
    },

    ownerUpdateRefresh() {
      this.selectedRequisitions = [];
      this.searchPaymentRequisitions(this.params);
    },

    exportPaymentRequisitionsCSV() {
      this.exportLoading = true;
      this.clearAllErrors();
      this.exportPaymentRequisitions(this.params)
        .then((data: Blob) => {
          const fileName = `payment-requisitions-${moment(new Date()).format(
            'YYYY-MM-DD-HH-mm-ss'
          )}`;
          createFile(data, fileName, 'csv');
        })
        .catch(error => {
          this.errorMessage = ErrorManager.extractApiError(error);
        })
        .finally(() => {
          this.exportLoading = false;
        });
    }
  }
});
